import React, { useEffect, useState, memo, useCallback } from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Icon from '../../../icons/Icon';
import { ICONS } from '../../../icons';
import CommonTextarea from '../../../components/common-textarea/CommonTextarea';
import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import { changeEditableMessage, setEditableMessage, setIsDirty } from '../../../redux/scout/scoutReducer';
import { REACTIONS, SCOUT_CREATE_TYPE, SCOUT_TABS } from '../../../constants';
import CardMark from './CardMark';

import scss from './MessageCard.module.scss';

const MessageCard = ({
  message: messageProps = {},
  onEditStart,
  isLoading,
  onSelect,
  onDelete,
  onInfo,
  onLike,
  onRestore,
  isActionsDisabled,
  onSaveMessage,
}) => {
  const { t } = useTranslation('scout');
  const dispatch = useDispatch();

  const { message, id, reaction, count, creator, newMessage, selected, createType } = messageProps;
  const editableMessage = useSelector((state) => state.scout.editableMessage);
  const scoutTab = useSelector((state) => state.scout.scoutTab);
  const [messageLocalText, setMessageLocalText] = useState(message);

  const isButtonsDisabled = isLoading || isActionsDisabled;

  const handleEditStart = () => {
    onEditStart({ id, message: message });
  };

  const handleSelect = () => {
    onSelect({ id: messageProps.id, selected: messageProps.selected });
  };

  const handleDelete = () => {
    onDelete(messageProps);
  };

  const handleEditClose = () => {
    dispatch(setEditableMessage({ id: null, message: '' }));
    dispatch(setIsDirty(false));
    setMessageLocalText(message);
  };

  const handleChangeText = (newTextValue) => {
    setMessageLocalText(newTextValue);
    dispatch(changeEditableMessage(newTextValue));
  };

  const handleSaveTextChanges = () => {
    dispatch(onSaveMessage());
  };

  const handleLike = () => {
    onLike({ id, reaction: reaction === REACTIONS.LIKE ? '' : REACTIONS.LIKE, createType });
  };

  const handleShowInfo = () => {
    onInfo(messageProps);
  };

  const handleRestoreMessage = () => {
    onRestore(id);
  };

  const isMarked = useCallback(
    (type) =>
      (scoutTab === SCOUT_TABS.FINAL_MESSAGES ||
        scoutTab === SCOUT_TABS.BACKUP ||
        scoutTab === SCOUT_TABS.SCREENED_MESSAGES) &&
      createType === type,
    [scoutTab, createType],
  );

  useEffect(() => {
    dispatch(setIsDirty(messageLocalText !== message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, messageLocalText]);

  useEffect(() => {
    setMessageLocalText(message);
  }, [message]);

  return (
    <div className={scss.cardWrapper}>
      <div className={scss.leftContent}>
        <div className={scss.idCell}>{count}</div>

        <div className={scss.contentWrapper}>
          {editableMessage.id === id ? (
            <div className={scss.inputCell}>
              <CommonTextarea value={messageLocalText} onChange={handleChangeText} rows='3' />

              <div className={scss.inputCellActions}>
                <CommonButton styleColor='secondary' maxWidth={120} onClick={handleEditClose}>
                  {t('discard')}
                </CommonButton>

                <CommonButton styleColor='primary' maxWidth={120} onClick={handleSaveTextChanges}>
                  {t('saveChanges')}
                </CommonButton>
              </div>
            </div>
          ) : (
            <div className={scss.textCell}>{message}</div>
          )}
        </div>
      </div>

      {scoutTab === SCOUT_TABS.BACKUP ? (
        <CommonButton styleColor={BUTTON_STYLES.SECONDARY} maxWidth={150} onClick={handleRestoreMessage}>
          {t('restoreMessage')}
          <Icon icon={ICONS.RoundArrow} disableCommonColors className={scss.reclaimIcon} />
        </CommonButton>
      ) : (
        <div className={scss.actionsCell}>
          <IconButton
            onClick={handleSelect}
            color='inherit'
            classes={{ root: scss.iconButton }}
            disabled={isButtonsDisabled}>
            <Icon
              icon={ICONS.PointerToTheRight}
              disableCommonColors
              className={clsx(scss.strokeIcon, scss.pointerIcon, selected && scss.strokeIconSelected)}
            />
          </IconButton>

          <IconButton
            onClick={handleShowInfo}
            color='inherit'
            classes={{ root: scss.iconButton }}
            disabled={isButtonsDisabled}>
            <Icon icon={ICONS.Info} disableCommonColors className={scss.strokeIcon} />
          </IconButton>

          <IconButton
            onClick={handleEditStart}
            color='inherit'
            classes={{ root: scss.iconButton }}
            disabled={isButtonsDisabled}>
            <Icon icon={ICONS.Edit} disableCommonColors className={scss.fillIcon} />
          </IconButton>

          <IconButton
            onClick={handleLike}
            color='inherit'
            classes={{ root: scss.iconButton }}
            disabled={isButtonsDisabled}>
            <Icon
              icon={ICONS.Like}
              disableCommonColors
              className={clsx(scss.strokeIcon, reaction === REACTIONS.LIKE && scss.strokeIconSelected)}
            />
          </IconButton>

          <IconButton
            onClick={handleDelete}
            color='inherit'
            classes={{ root: scss.iconButton }}
            disabled={isButtonsDisabled}>
            <Icon icon={ICONS.Delete} disableCommonColors className={scss.fillIcon} />
          </IconButton>
        </div>
      )}

      <CardMark
        isManual={creator === 'MANUAL'}
        isNew={newMessage}
        isGenerated={isMarked(SCOUT_CREATE_TYPE.GENERATED)}
        isCompressed={isMarked(SCOUT_CREATE_TYPE.COMPRESSED)}
      />
    </div>
  );
};

export default memo(MessageCard);
